<template>
  <div class="app-shell">
    <!-- 手機版左上：側欄開關 -->
    <button
      class="menu-toggle"
      :class="{ open: isMobileNavOpen }"
      type="button"
      :aria-label="isMobileNavOpen ? '關閉選單' : '開啟選單'"
      :aria-expanded="isMobileNavOpen ? 'true' : 'false'"
      @click="toggleNav"
    >
      <!-- chevron_right / chevron_left -->
      <svg
        class="chevron"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <!-- 收合：向右 -->
        <path
          v-if="!isMobileNavOpen"
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <!-- 展開：向左 -->
        <path
          v-else
          d="M15 6l-6 6 6 6"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>


    <!-- 手機版：開啟側欄時的遮罩 -->
    <div v-if="isMobileNavOpen" class="backdrop" @click="closeNav" />

    <aside :class="['sidebar', { open: isMobileNavOpen }]" id="mobile-sidebar">
      <div class="brand">TSK</div>
      <nav class="nav-container" ref="navRef" @click="closeNavOnNavigate">
        <!-- 滑動背景 -->
        <div class="nav-glider" :style="gliderStyle"></div>

        <router-link
          v-for="item in visibleNavItems"
          :key="item.key"
          :class="['nav-item', { active: isNavItemActive(item) }]"
          :to="{ name: item.routeName }"
          :aria-label="$t(item.i18nKey)"
          @click.prevent="onClickNav(item)"
        >
          <span class="nav-text">{{ $t(item.i18nKey) }}</span>
        </router-link>
      </nav>
      <!-- <button class="logout" @click="onSignOut">{{ $t('common.logout') }}</button> -->
    </aside>

    <main class="content">
      <router-view />
    </main>

    <!-- ★ 身分驗證提示彈窗（未驗證） -->
    <div v-if="showVerifyModal" class="modal-backdrop" @click.self="closeVerifyModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="verify-title">
        <h3 id="verify-title" class="modal-title">{{ $t('verifyModal.title') }}</h3>
        <p class="modal-text">
          {{ $t('verifyModal.line1') }}<br>
          {{ $t('verifyModal.line2') }}<br>
          <a class="inline-link" :href="termsHref" target="_blank" rel="noopener">
            {{ $t('verifyModal.linkPrefix') }}{{ $t('member.Privacy') }}{{ $t('verifyModal.linkSuffix') }}
          </a>
        </p>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="confirmAndGoProfile">
            {{ $t('verifyModal.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ★ 公告輪播彈窗（已驗證；同分頁只顯示一次） -->
    <div v-if="showAnnouncementModal" class="modal-backdrop" @click.self="closeAnnouncement">
      <div
        class="announcement-card"
        :class="{ 'has-banner': bannerSlide }"
        role="dialog"
        aria-modal="true"
        aria-label="當期活動公告"
        @mouseenter="pauseSlides"
        @mouseleave="resumeSlides"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <button class="announcement-close" aria-label="關閉公告" @click="closeAnnouncement">×</button>

        <!-- ✅ 有 bannerSlide：在輪播上方多顯示橫幅 -->
        <div
          v-if="bannerSlide"
          class="banner-wrapper"
          @click="onBannerClick"
        >
          <component
            :is="bannerHref ? 'a' : 'div'"
            :href="bannerHref || null"
            class="banner-slide"
          >
            <img
              class="banner-img"
              :src="bannerSlide.image_url"
              :alt="bannerSlide.title || 'TSK 活動橫幅'"
            />
          </component>
        </div>

        <!-- 🎠 輪播：只有在有非 banner 活動時顯示 -->
        <div
          v-if="promoSlides.length"
          class="carousel"
          @click="onCarouselClick"
        >
          <component
            :is="currentSlideHref ? 'a' : 'div'"
            :href="currentSlideHref || null"
            class="carousel-slide"
          >
            <img
              class="carousel-img"
              :src="currentSlide.image_url"
              :alt="currentSlide.title || 'TSK 當期活動'"
            />
          </component>

          <button
            v-if="promoSlides.length > 1"
            class="nav prev"
            @click="prevSlide"
            aria-label="上一張"
          >‹</button>

          <button
            v-if="promoSlides.length > 1"
            class="nav next"
            @click="nextSlide"
            aria-label="下一張"
          >›</button>

          <div v-if="promoSlides.length > 1" class="dots">
            <button
              v-for="(s, idx) in promoSlides"
              :key="s.id || idx"
              class="dot"
              :class="{ active: idx === slideIndex }"
              @click="goSlide(idx)"
              :aria-label="`第 ${idx+1} 張`"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import { appShellNavItems } from '@/config/AppShellNav'

export default {
  name: 'AppShell',
  data() {
    return {
      isMobileNavOpen: false,
      showVerifyModal: false,
      verifyModalReason: 'enter-app',
      lastKnownIdentityStatus: null,
      fetchingStatus: false,

      // ★ 是否有當期活動（決定是否顯示選單）
      hasActivePromo: false,

      // ★ 公告輪播
      showAnnouncementModal: false,
      promoSlides: [],         // [{ id,title,image_url,link_url }]
      slideIndex: 0,
      slideTimer: null,
      slideAutoplayMs: 3500,

      bannerSlide: null,

      // 觸控滑動
      touchStartX: 0,
      touchDeltaX: 0,

      // 滑動背景樣式
      gliderStyle: {
        top: '0px',
        height: '0px',
        opacity: 0
      }
    }
  },

  async beforeRouteEnter(to, from, next) {
    const { data } = await supabase.auth.getSession()
    if (!data?.session) next('/member')
    else next()
  },

  async mounted() {
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)

    await this.refreshActivePromoFlag()   // 是否有當期活動
    await this.checkIdentityOnEnter()     // 進殼層判斷是否彈窗
    await this.checkIdentityOnRoute(this.$route)
    this.$nextTick(() => this.updateGlider())
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.body.style.overflow = ''
    this.clearSlideTimer()
  },

  watch: {
    $route(to) {
      this.closeNav()
      this.checkIdentityOnRoute(to)
      this.$nextTick(() => this.updateGlider())
    },
    hasActivePromo() {
      this.$nextTick(() => this.updateGlider())
    },
    isMobileNavOpen() { this.updateBodyScrollLock() },
    showVerifyModal() { this.updateBodyScrollLock() },
    showAnnouncementModal(val) {
      this.updateBodyScrollLock()
      if (val) {
        this.startSlides()
      } else {
        this.clearSlideTimer()
      }
    },
  },

  computed: {
    visibleNavItems() {
      return appShellNavItems.filter((item) => {
        if (typeof item.show === 'function') {
          // show(ctx) → ctx 是 this
          return item.show(this)
        }
        return true
      })
    },
    
    currentSlide() {
      if (!this.promoSlides.length) {
        return { image_url: '', link_url: '', title: '' }
      }
      return this.promoSlides[this.slideIndex]
    },
    termsHref() {
      return this.$router.resolve({ name: 'TermsPrivacy' }).href
    },
    // ✅ 目前輪播那張要開的新視窗網址
    currentSlideHref() {
      return this.buildPromoDetailHref(this.currentSlide)
    },

    // ✅ banner 要開的新視窗網址
    bannerHref() {
      return this.buildPromoDetailHref(this.bannerSlide)
    }
  },

  methods: {
    // ====== 新版：統一選單點擊處理 ======
    async onClickNav(item) {
      // 如果有 gatedReason，就走驗證流程
      if (item.gatedReason) {
        const ok = await this.ensureVerifiedOrPrompt(item.gatedReason)
        if (!ok) return
      }

      // 再導到對應頁面
      this.$router.push({ name: item.routeName })
    },

    // 給 glider & active 樣式用
    isNavItemActive(item) {
      const path = this.$route.path
      if (Array.isArray(item.activePrefixes)) {
        return item.activePrefixes.some((prefix) => path.startsWith(prefix))
      }
      return false
    },
    
    // ====== 左側「當期活動」選單：是否顯示 ======
    async refreshActivePromoFlag() {
      try {
        const nowIso = new Date().toISOString()
        const { data: rows, error } = await supabase
          .from('promotions')
          .select('id')
          .in('status', ['scheduled','published'])
          .lte('starts_at', nowIso)
          .or('ends_at.is.null,ends_at.gte.' + nowIso)
          .limit(1)

        if (error) throw error
        this.hasActivePromo = (rows?.length > 0)
      } catch (e) {
        console.debug('[refreshActivePromoFlag] skipped', e?.message || e)
        this.hasActivePromo = false
      }
    },

    // ====== 當期活動入口攔截 ======
    async onClickPromo() {
      const ok = await this.ensureVerifiedOrPrompt('promo')
      if (ok) this.$router.push({ name: 'PromoList' })
    },

    // ====== 產品總覽入口攔截 ======
    async onClickProducts() {
      const ok = await this.ensureVerifiedOrPrompt('products')
      if (ok) this.$router.push({ name: 'AppProducts' })
    },

    // ====== 精選分享入口攔截 ======
    async onClickPapers () {
      const ok = await this.ensureVerifiedOrPrompt('papers')
      if (ok) {
        this.$router.push({ name: 'AppPapers' })
      }
    },

    // ====== 影片入口攔截 ======
    async onClickVideos() {
      const ok = await this.ensureVerifiedOrPrompt('video')
      if (ok) this.$router.push({ name: 'VideoIndex' })
    },

    // ⭐ 新增：醫師分享入口攔截
    async onClickShare() {
      const ok = await this.ensureVerifiedOrPrompt('share')
      if (ok) this.$router.push({ name: 'AppShare' })
    },

    // ⭐ 新增：下載專區入口攔截
    async onClickDownload() {
      const ok = await this.ensureVerifiedOrPrompt('download')
      if (ok) this.$router.push({ name: 'AppDownload' })
    },

    async ensureVerifiedOrPrompt(reason = 'video') {
      try {
        const status = await this.fetchIdentityStatus()
        if (status === 'verified') return true
        this.verifyModalReason = reason
        this.showVerifyModal = true
        return false
      } catch (e) {
        this.verifyModalReason = reason
        this.showVerifyModal = true
        return false
      }
    },

    async fetchIdentityStatus() {
      if (this.fetchingStatus) return this.lastKnownIdentityStatus
      try {
        this.fetchingStatus = true
        const { data: sess } = await supabase.auth.getSession()
        const uid = sess?.session?.user?.id
        if (!uid) return null

        const { data, error } = await supabase
          .from('user_profiles')
          .select('identity_status')
          .eq('id', uid)
          .maybeSingle()

        if (error) {
          console.debug('[fetchIdentityStatus] error', error.message)
          return this.lastKnownIdentityStatus
        }

        const status = (data?.identity_status || 'unverified').toLowerCase()
        this.lastKnownIdentityStatus = status
        return status
      } finally {
        this.fetchingStatus = false
      }
    },

    // ====== 進入殼層時的提示邏輯 ======
    async checkIdentityOnEnter() {
      try {
        const status = await this.fetchIdentityStatus()
        const onProfile = this.$route.name === 'AppProfile'

        // 未驗證：同分頁只提醒一次
        if (status !== 'verified' && !onProfile) {
          if (sessionStorage.getItem('id_prompt_shown') !== '1') {
            this.verifyModalReason = 'enter-app'
            this.showVerifyModal = true
            sessionStorage.setItem('id_prompt_shown', '1')
          }
          return
        }

        // 已驗證：同分頁只顯示一次公告
        if (status === 'verified') {
          if (sessionStorage.getItem('announcement_shown') !== '1') {
            await this.loadPromoSlides()
            if (this.promoSlides.length) {
              this.slideIndex = 0
              this.showAnnouncementModal = true
              sessionStorage.setItem('announcement_shown', '1')
            }
          }
        }
      } catch (e) {
        console.debug('[checkIdentityOnEnter] skipped', e?.message || e)
      }
    },

    // 抓取當期活動的輪播素材
    async loadPromoSlides() {
      try {
        const nowIso = new Date().toISOString()
        const { data, error } = await supabase
          .from('promotions')
          .select('id,title,image_url,link_url,starts_at,is_featured,is_banner')
          .in('status', ['scheduled','published'])
          .lte('starts_at', nowIso)
          .or('ends_at.is.null,ends_at.gte.' + nowIso)
          .order('is_featured', { ascending: false })
          .order('starts_at', { ascending: false })

        if (error) throw error

        const withImage = (data || []).filter(x => x.image_url && x.image_url.trim())

        // ✅ 所有 is_banner 的活動（可以未來延伸成多個 banner，用第一筆就好）
        const bannerItems = withImage.filter(x =>
          x.is_banner === true || x.is_banner === 1 || x.is_banner === 'true'
        )

        // ✅ 輪播只吃「非 banner 的活動」
        this.promoSlides = withImage.filter(x =>
          !(x.is_banner === true || x.is_banner === 1 || x.is_banner === 'true')
        )

        // ✅ 目前只用第一個 banner
        this.bannerSlide = bannerItems[0] || null
      } catch (e) {
        console.debug('[loadPromoSlides] skipped', e?.message || e)
        this.promoSlides = []
        this.bannerSlide = null
      }
    },

    onCarouselClick(e) {
      if (e.target.closest('.nav') || e.target.closest('.dot')) return

      const go = () => {
        this.closeAnnouncement()
        const promoId = this.currentSlide.id
        this.$router.push({ name: 'PromoList', query: { promo: promoId } })
      }

      this.ensureVerifiedOrPrompt('promo').then(ok => {
        if (ok) go()
      })
    },

    onBannerClick(e) {
      // 如果點到的是 <a> 裡的東西就讓瀏覽器自己處理外連
      if (e.target.closest('a')) return

      const go = () => {
        this.closeAnnouncement()
        const promoId = this.bannerSlide?.id
        if (promoId) {
          this.$router.push({ name: 'PromoList', query: { promo: promoId } })
        } else {
          this.$router.push({ name: 'PromoList' })
        }
      }

      this.ensureVerifiedOrPrompt('promo').then(ok => {
        if (ok) go()
      })
    },

    buildPromoDetailHref(slide) {
      if (!slide) return null

      const raw = (slide.link_url || '').trim()

      // 1. 外部連結，就走外部連結
      if (raw && /^https?:\/\//i.test(raw)) {
        return raw
      }

      // 2. 內部路徑 → 萃取最後一段 folder
      let folder = ''
      if (raw) {
        const stripped = raw.replace(/^\/+|\/+$/g, '')
        const parts = stripped.split('/')
        folder = parts[parts.length - 1]
      }

      // 3. 若沒有 folder，就保持原本行為（項目閃動，不跳 detail）
      if (!folder) return null

      // 4. 組 promo/detail/:folder
      return this.$router.resolve({
        name: 'PromoDetail',
        params: { folder }
      }).href
    },


    // ====== 切頁時：需要驗證的頁面再提醒 ======
    async checkIdentityOnRoute(to) {
      try {
        const name = to?.name
        // 需要驗證的頁面：影片 + 當期活動 + 產品總覽 + 精選分享 + 醫師分享 + 下載專區
        const gated = [
          'VideoIndex', 'TopicVideos', 'VideoSubtopic', 'VideoPlay',
          'PromoList', 'AppProducts', 'AppPapers',
          'AppShare', 'AppDownload','AppTrial',
        ]
        if (!gated.includes(name)) return

        const status = await this.fetchIdentityStatus()
        const onProfile = name === 'AppProfile'
        if (status !== 'verified' && !onProfile) {
          if (name === 'PromoList')           this.verifyModalReason = 'promo'
          else if (name === 'AppProducts')    this.verifyModalReason = 'products'
          else if (name === 'AppPapers')      this.verifyModalReason = 'papers'
          else if (name === 'AppShare')       this.verifyModalReason = 'share'
          else if (name === 'AppDownload')    this.verifyModalReason = 'download'
          else if (name === 'AppTrial')       this.verifyModalReason = 'trial'
          else                                 this.verifyModalReason = 'video'

          this.showVerifyModal = true
        }
      } catch (e) {
        console.debug('[checkIdentityOnRoute] skipped', e?.message || e)
      }
    },

    // ====== 公告 modal 控制 ======
    closeAnnouncement() { this.showAnnouncementModal = false },

    // ====== 輪播控制 ======
    startSlides() {
      this.clearSlideTimer()
      if (this.promoSlides.length <= 1) return
      this.slideTimer = setInterval(this.nextSlide, this.slideAutoplayMs)
    },
    clearSlideTimer() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },
    pauseSlides() { this.clearSlideTimer() },
    resumeSlides() { if (this.showAnnouncementModal) this.startSlides() },
    nextSlide() {
      if (!this.promoSlides.length) return
      this.slideIndex = (this.slideIndex + 1) % this.promoSlides.length
    },
    prevSlide() {
      if (!this.promoSlides.length) return
      this.slideIndex = (this.slideIndex - 1 + this.promoSlides.length) % this.promoSlides.length
    },
    goSlide(idx) {
      if (idx >= 0 && idx < this.promoSlides.length) this.slideIndex = idx
    },

    // 觸控滑動（手機）
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchDeltaX = 0
      this.pauseSlides()
    },
    onTouchMove(e) {
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX
    },
    onTouchEnd() {
      const threshold = 40
      if (this.touchDeltaX > threshold) this.prevSlide()
      else if (this.touchDeltaX < -threshold) this.nextSlide()
      this.resumeSlides()
      this.touchStartX = 0
      this.touchDeltaX = 0
    },

    // ====== 會員驗證 modal 控制 ======
    closeVerifyModal() { this.showVerifyModal = false },
    confirmAndGoProfile() {
      this.showVerifyModal = false
      if (this.$route.name !== 'AppProfile') {
        this.$router.replace({ name: 'AppProfile' })
      }
    },

    // ====== 其餘 ======
    async onSignOut() {
      await supabase.auth.signOut()
      this.$router.replace('/member')
    },

    // 側欄（手機）
    toggleNav() { this.isMobileNavOpen = !this.isMobileNavOpen },
    closeNav() { if (this.isMobileNavOpen) this.isMobileNavOpen = false },
    closeNavOnNavigate(e) { if (e.target && e.target.closest('a')) this.closeNav() },

    // 共用：鎖定 body 滾動
    updateBodyScrollLock() {
      const anyOpen = this.isMobileNavOpen || this.showVerifyModal || this.showAnnouncementModal
      document.body.style.overflow = anyOpen ? 'hidden' : ''
    },

    // 鍵盤關閉 / 切換
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.showVerifyModal) this.closeVerifyModal()
        else if (this.showAnnouncementModal) this.closeAnnouncement()
        else this.closeNav()
      }
      if (this.showAnnouncementModal && this.promoSlides.length > 1) {
        if (e.key === 'ArrowLeft') this.prevSlide()
        if (e.key === 'ArrowRight') this.nextSlide()
      }
    },

    // 視窗放大自動關閉抽屜
    onResize() {
      if (window.innerWidth >= 900) this.closeNav()
      this.updateGlider()
    },

    // 更新滑動背景位置
    updateGlider() {
      const nav = this.$refs.navRef
      if (!nav) return

      // 因為 active class 是動態綁定的，DOM 更新可能需要一點時間
      // 這裡假設 updateGlider 是在 $nextTick 呼叫的，所以應該抓得到
      const activeLink = nav.querySelector('.nav-item.active')
      if (activeLink) {
        const navRect = nav.getBoundingClientRect()
        const linkRect = activeLink.getBoundingClientRect()

        const top = linkRect.top - navRect.top
        const height = linkRect.height

        this.gliderStyle = {
          top: `${top}px`,
          height: `${height}px`,
          opacity: 1
        }
      } else {
        this.gliderStyle = {
          ...this.gliderStyle,
          opacity: 0
        }
      }
    },

    // 判斷導航是否啟用（包含子路徑）
    isNavActive(section) {
      const path = this.$route.path
      switch (section) {
        case 'promo':
          return path.startsWith('/app/promo')
        case 'products':
          return path.startsWith('/app/products') || path.startsWith('/app/product/')
        case 'papers':
          return path.startsWith('/app/papers') || path.startsWith('/app/paper/')
        case 'videos':
          return path.startsWith('/app/videos') || path.startsWith('/app/video/')
        case 'share':
          return path.startsWith('/app/share')
        case 'download':
          return path.startsWith('/app/download')
        case 'profile':
          return path.startsWith('/app/profile')
        default:
          return false
      }
    }
  }
}
</script>

<style scoped>
.app-shell {
  --app-top-offset: var(--header-height, 0px);
  display: flex;
  margin-top: var(--app-top-offset);
  min-height: calc(100vh - var(--app-top-offset));
  background: #f7f7f7;
  box-sizing: border-box;
}

/* ===== 桌機側欄 / 內容 ===== */
.sidebar {
  width: 260px;
  background: #ffffff;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;

  position: sticky;
  top: 60px;
  height: calc(100vh - 60px);

  padding: calc(var(--app-top-offset) + 16px) 16px 16px;
  box-sizing: border-box;
}

.brand {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  letter-spacing: 0.08em;
}

.nav-container {
  position: relative;
}

.nav-glider {
  position: absolute;
  left: 0;
  right: 0;
  background: #111;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 0;
  pointer-events: none;
}

.nav-item {
  position: relative;
  display: block;
  padding: 10px 12px;
  border-radius: 10px;
  color: #333;
  text-decoration: none;
  margin-bottom: 6px;
  z-index: 1;
  transition: color 0.2s;
}
.nav-item.active {
  background: transparent;
  color: #fff;
}
.nav-text {
  position: relative;
  z-index: 2;
}

.logout {
  margin-top: auto;
  margin-bottom: 20px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 0;
  background: #eee;
  cursor: pointer;
}

.content {
  flex: 1;
  padding: 24px;
}

/* 手機開關按鈕（桌機隱藏） */
.menu-toggle { display: none; }

/* === 身分驗證提示彈窗 === */
.modal-backdrop {
  position: fixed;
  inset: var(--app-top-offset) 0 0 0;
  background: rgba(0,0,0,.35);
  z-index: 1300;
  display: grid;
  place-items: center;
  padding: 20px;
}

.modal-card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(0,0,0,.22);
  padding: 20px 18px;
}

.modal-title { margin: 0 0 10px; font-size: 18px; font-weight: 700; }
.modal-text { margin: 0 0 14px; color: #444; line-height: 1.6; }
.modal-actions { display: flex; justify-content: center; gap: 10px; }
.btn.btn-primary { background: #111; color: #fff; border: 0; padding: 10px 14px; border-radius: 10px; cursor: pointer; }

/* === 公告輪播彈窗 === */
.announcement-card {
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(0,0,0,.22);
  position: relative;
  padding: 0;
  overflow: hidden;
}


.announcement-close {
  position: absolute; right: 8px; top: 8px; width: 34px; height: 34px; border: 0; border-radius: 10px;
  background: rgba(255,255,255,.92); font-size: 20px; line-height: 1; cursor: pointer;
  box-shadow: 0 4px 14px rgba(0,0,0,.18); z-index: 2;
}
.announcement-close:active { transform: translateY(1px); }

.carousel { position: relative; cursor: pointer; }
.carousel-slide { display: block; }
.carousel-img {
  display: block;
  width: 100%;
  height: auto;
  max-height: min(78vh, 880px);
  object-fit: contain;
  background: #fff;
}

/* Prev/Next */
.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 38px; height: 38px;
  border: 0; border-radius: 50%;
  background: rgba(255,255,255,.95);
  box-shadow: 0 6px 18px rgba(0,0,0,.18);
  cursor: pointer; z-index: 2;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 22px; line-height: 1;
}
.nav.prev { left: 10px; }
.nav.next { right: 10px; }
.nav:active { transform: translateY(-50%) scale(.98); }

/* Dots */
.dots {
  position: absolute; left: 0; right: 0; bottom: 10px;
  display: flex; justify-content: center; gap: 8px; z-index: 2;
}
.dot {
  width: 8px; height: 8px; border-radius: 50%;
  border: 0; background: rgba(0,0,0,.22); cursor: pointer;
}
.dot.active { background: rgba(0,0,0,.75); }

/* ===== 手機版 ===== */
@media (max-width: 899px) {
  .app-shell { display: block; }
  .content { padding: 16px; }

  .menu-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 60px;

    /* ✅ 收合：貼齊最左邊 */
    left: 0;

    width: 42px;
    height: 42px;

    /* ✅ 方型 + 右半圓角 */
    border-radius: 0 14px 14px 0;

    /* ✅ 無邊框 */
    border: 0;

    background: #fff;
    box-shadow: 0 10px 24px rgba(0,0,0,.12);
    z-index: 1101;
    color: #0b69b7;
    -webkit-tap-highlight-color: transparent;

    transition: left .25s ease, transform .25s ease, box-shadow .25s ease;
  }

  /* sidebar 開啟時：按鈕貼著 sidebar 右邊界，並跨在邊界上 */
  .menu-toggle.open {
    /* sidebar 寬度 = min(80vw, 320px) */
    left: clamp(0px, 80vw, 320px);
    transform: none;
  }

  .menu-toggle .chevron {
    display: block;
    width: 24px !important;
    height: 24px !important;
    transition: transform .2s ease;
  }

  .menu-toggle .chevron path {
    vector-effect: non-scaling-stroke;
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* 你原本有這段會把 open 的 transform 也清掉，改成只清非 open */
  @supports (transform: translateX(0)) {
    .menu-toggle:not(.open) {
      transform: none !important;
    }
  }

  img.menu-icon, .menu-toggle svg {
    max-width: none !important;
    height: auto !important;
  }

  .sidebar {
    position: fixed;
    top: var(--app-top-offset);
    left: 0;
    height: calc(100vh - var(--app-top-offset));
    width: 80vw;
    max-width: 320px;

    transform: translateX(-100%);
    transition: transform .25s ease;
    box-shadow: 0 12px 30px rgba(0,0,0,.18);
    z-index: 1090;
    background: #ffffff;

    border-right: 1px solid #eee;
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
  }

  .sidebar.open { transform: translateX(0); }

  .backdrop {
    position: fixed;
    inset: var(--app-top-offset) 0 0 0;
    background: rgba(0,0,0,.35);
    z-index: 1080;
  }
}

.banner-wrapper {
  cursor: pointer;
  margin-bottom: 12px;           /* 🔹跟下方輪播拉開距離 */
  padding-bottom: 8px;           /* 🔹內縮一點點 */
}

.banner-slide {
  display: block;
}

.banner-img {
  display: block;
  width: 100%;
  height: auto;
  max-height: min(60vh, 600px);
  object-fit: contain;
  background: #fff;
}

/* 有 banner 時：整個彈窗縮窄，畫面看起來比較「直」 */
.announcement-card.has-banner {
  max-width: 450px;          /* 比原本 720 再窄一點 */
}

/* 有 banner 時：兩張圖一律跟卡片同寬，不拉伸比例 */
.announcement-card.has-banner .banner-img,
.announcement-card.has-banner .carousel-img {
  display: block;
  width: 100%;
  height: auto;              /* 靠原始比例決定高度，不扯歪 */
  object-fit: contain;
}

/* 上下分配視窗高度：上 3 成，下 5 成，整體偏「直」 */
.announcement-card.has-banner .banner-img {
  max-height: 30vh;
}

.announcement-card.has-banner .carousel-img {
  max-height: 50vh;
}


</style>
